import { useMemo } from 'react';
import useVersionManagementV1 from './useVersionManagementV1';
import { parsePerformanceTestingSections } from '../utils/markdownSectionParser';

/**
 * Custom hook to manage performance testing versions
 * Stores and tracks different versions of performance tests from regenerations
 *
 * Uses useVersionManagementV1 which fixes:
 * - Local edit detection via isLocalUpdateRef
 * - Proper version tracking without creating duplicates on edit
 */
const usePerformanceTestingVersions = (conversation, isStreaming, streamingContent, isLocalUpdateRef = null) => {
  // Configuration for performance testing version management
  const performanceTestingConfig = {
    // Determine if a message contains performance testing content
    isRelevantMessage: (msg) => {
      const content = msg.content || '';
      return content.includes('Performance Testing') ||
             content.includes('Performance Test') ||
             content.includes('Load Testing') ||
             content.includes('Stress Testing') ||
             content.includes('Scalability') ||
             content.includes('Response Time') ||
             content.includes('Throughput') ||
             content.includes('###') ||
             content.includes('##');
    },

    // Parse performance testing sections from content
    parseContent: (content, msg) => {
      if (msg?.parsed_content?.sections?.length) {
        return msg.parsed_content.sections;
      }
      return parsePerformanceTestingSections(content);
    },

    // Extract version information from a message
    // Note: The id will be set by useVersionManagementV1 based on messageIndex
    extractVersions: (msg, parsedTestCases, versionNumber, isPreview = false) => {
      return {
        id: null, // Will be overwritten by useVersionManagementV1 with messageIndex-based ID
        timestamp: msg.createdAt || new Date().toISOString(),
        testCases: parsedTestCases,
        content: msg.content,
        versionNumber: versionNumber,
        isPreview: isPreview,
        label: isPreview ? 'Generating...' : undefined
      };
    },

    conversation,
    isStreaming,
    streamingContent,
    isLocalUpdateRef
  };

  // Use the V1 version management hook that supports isLocalUpdateRef
  const versionData = useVersionManagementV1(performanceTestingConfig);

  // Memoize the currentVersion to prevent unnecessary re-renders
  const currentVersion = useMemo(() => {
    if (!versionData.currentVersion) return null;
    return {
      ...versionData.currentVersion,
      testCases: versionData.currentVersion.parsedContent || versionData.currentVersion.testCases
    };
  }, [versionData.currentVersion]);

  // Map the generic version data to performance testing-specific structure
  return {
    versions: versionData.versions,
    currentVersion,
    currentVersionIndex: versionData.currentVersionIndex,
    setCurrentVersionIndex: versionData.setCurrentVersionIndex,
    hasVersions: versionData.hasVersions,
    isLatestVersion: versionData.isLatestVersion,
    addVersion: versionData.addVersion,
    updateCurrentVersion: versionData.updateCurrentVersion
  };
};

export default usePerformanceTestingVersions;
